// WP13 Gate 3 — canonical Company Detail. Answers "Where and how is this
// company observed in the market?" using only published facts: descriptive
// counts, sparse weekly activity, and within-company footprint shares.
// Never company strength, leadership, dominance, domicile or relationships.
import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useWp12 } from "./useWp12";
import InfoTip from "./InfoTip";
import { zoneColor } from "./LeadersChart";
import { CompositionBar } from "./charts";

const Section = ({ title, tip, tipId, children, testId }) => (
    <section className="space-y-2" data-testid={testId}>
        <h2 className="text-sm font-semibold inline-flex items-center gap-2">
            {title}
            {tip && <InfoTip label={`${title} explanation`} testId={tipId}>{tip}</InfoTip>}
        </h2>
        {children}
    </section>
);

const Stat = ({ label, value, testId }) => (
    <div data-testid={testId}>
        <div className="text-[8.5px] uppercase tracking-wider text-[hsl(var(--mute))]">{label}</div>
        <div className="num text-[13px] font-semibold mt-0.5">{value ?? "—"}</div>
    </div>
);

// Full week axis between first and last observed week — presentation only.
// Weeks without a published observation render no bar (missing ≠ zero).
const weekAxis = (start, end) => {
    const m1 = /^(\d{4})-W(\d{2})$/.exec(start || ""), m2 = /^(\d{4})-W(\d{2})$/.exec(end || "");
    if (!m1 || !m2) return [];
    let y = +m1[1], w = +m1[2];
    const out = [];
    while ((y < +m2[1] || (y === +m2[1] && w <= +m2[2])) && out.length < 160) {
        out.push(`${y}-W${String(w).padStart(2, "0")}`);
        w += 1; if (w > 52) { w = 1; y += 1; }
    }
    return out;
};

const ActivityTimeline = ({ timeline, stats }) => {
    const rows = useMemo(() => {
        const byWeek = Object.fromEntries((timeline || []).map((t) => [t.yearWeek, t.evidenceCount]));
        return weekAxis(stats?.firstSeenWeek, stats?.lastSeenWeek).map((wk) => ({
            week: wk, count: byWeek[wk] ?? null,
        }));
    }, [timeline, stats]);
    if (rows.length < 2) return null;
    return (
        <div className="card-flat p-4" data-testid="wp13-company-activity">
            <ResponsiveContainer width="100%" height={160}>
                <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--line))" vertical={false} />
                    <XAxis dataKey="week" tick={{ fontSize: 9, fill: "hsl(var(--mute))" }}
                           tickFormatter={(w) => String(w).replace("2026-", "")} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                    <YAxis allowDecimals={false} tick={{ fontSize: 9, fill: "hsl(var(--mute))" }} tickLine={false} axisLine={false} width={26} />
                    <Tooltip contentStyle={{ fontSize: 10, borderRadius: 6, border: "1px solid hsl(var(--line))" }}
                             formatter={(v) => [v, "Evidence"]} cursor={{ fill: "hsl(var(--surface-sunken))" }} />
                    <Bar dataKey="count" fill="hsl(var(--accent))" fillOpacity={0.7} isAnimationActive={false} />
                </BarChart>
            </ResponsiveContainer>
            <p className="text-[9px] text-[hsl(var(--mute))] mt-1">
                Published evidence activity per week. Weeks without a published observation show no bar — a missing observation is not zero. Activity volume reflects observation coverage, not company strength.
            </p>
        </div>
    );
};

// Zone → Use Case → Pattern within-company footprint.
const MarketFootprint = ({ footprint }) => {
    const grouped = useMemo(() => {
        const zones = new Map();
        let total = 0;
        for (const f of footprint || []) {
            const cnt = Number(f.evidenceCount) || 0;
            total += cnt;
            const zk = f.marketZoneKey || "_unmapped";
            if (!zones.has(zk)) zones.set(zk, { key: zk, label: f.marketZoneLabel || "Unmapped", token: f.presentationColorToken, count: 0, useCases: new Map() });
            const z = zones.get(zk);
            z.count += cnt;
            const uk = f.useCaseKey || "_none";
            if (!z.useCases.has(uk)) z.useCases.set(uk, { key: uk, label: f.useCaseLabel || "—", count: 0, patterns: [] });
            const u = z.useCases.get(uk);
            u.count += cnt;
            u.patterns.push(f);
        }
        const list = [...zones.values()].sort((a, b) => b.count - a.count);
        for (const z of list) {
            z.useCaseList = [...z.useCases.values()].sort((a, b) => b.count - a.count);
            for (const u of z.useCaseList) u.patterns.sort((a, b) => (Number(b.evidenceCount) || 0) - (Number(a.evidenceCount) || 0));
        }
        return { list, total };
    }, [footprint]);
    if (!grouped.total) return null;
    const maxZone = grouped.list[0]?.count || 1;
    return (
        <div className="space-y-1.5" data-testid="wp13-company-footprint-zones">
            {grouped.list.map((z) => {
                const color = zoneColor(z.token || z.key);
                const share = ((z.count / grouped.total) * 100).toFixed(0);
                return (
                    <details key={z.key} className="card-flat" data-testid={`wp13-footprint-zone-${z.key}`}>
                        <summary className="px-4 py-2.5 cursor-pointer select-none flex items-center gap-3 [&::-webkit-details-marker]:hidden">
                            <span className="text-[hsl(var(--mute))] text-[9px] flex-shrink-0">▶</span>
                            <span className="text-[11.5px] font-medium w-52 truncate flex-shrink-0" title={z.label}>{z.label}</span>
                            <div className="flex-1 h-2.5 rounded-sm bg-[hsl(var(--surface-sunken))] overflow-hidden">
                                <div className="h-full rounded-sm" style={{ width: `${Math.max(2, (z.count / maxZone) * 100)}%`, background: color, opacity: 0.75 }} />
                            </div>
                            <span className="num text-[10px] w-20 text-right text-[hsl(var(--mute))] flex-shrink-0">{z.count} · {share}%</span>
                        </summary>
                        <div className="px-4 pb-3 space-y-2 border-t border-[hsl(var(--line))] pt-2">
                            {z.useCaseList.map((u) => (
                                <div key={u.key}>
                                    <div className="flex items-center gap-3">
                                        <span className="text-[10.5px] w-56 truncate pl-3 flex-shrink-0 text-[hsl(var(--ink-soft))]" title={u.label}>{u.label}</span>
                                        <div className="flex-1 h-1.5 rounded-sm bg-[hsl(var(--surface-sunken))] overflow-hidden">
                                            <div className="h-full rounded-sm" style={{ width: `${Math.max(2, (u.count / z.count) * 100)}%`, background: color, opacity: 0.5 }} />
                                        </div>
                                        <span className="num text-[9px] w-20 text-right text-[hsl(var(--mute))] flex-shrink-0">{u.count}</span>
                                    </div>
                                    <div className="pl-6 mt-1 space-y-0.5">
                                        {u.patterns.slice(0, 5).map((p) => (
                                            <div key={p.canonicalPatternId} className="flex items-baseline gap-2">
                                                <Link to={`/market-v2/patterns/${encodeURIComponent(p.canonicalPatternId)}`}
                                                      className="text-[10px] truncate hover:text-[hsl(var(--accent-ink))] hover:underline"
                                                      data-testid={`wp13-footprint-pattern-${p.canonicalPatternId}`}>
                                                    {p.displayTitle || p.patternLabel}
                                                </Link>
                                                <span className="num text-[9px] text-[hsl(var(--mute))] flex-shrink-0">{p.evidenceCount}</span>
                                            </div>
                                        ))}
                                        {u.patterns.length > 5 && <div className="text-[9px] text-[hsl(var(--mute))]">+ {u.patterns.length - 5} more patterns</div>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </details>
                );
            })}
            <p className="text-[9px] text-[hsl(var(--mute))]">
                Distribution of this company's pattern-linked observations across the market. Percentages are the share of this company's pattern-linked observations — one published evidence item may support multiple Patterns, so linked observations can exceed the number of unique Evidence records. Never market share, pattern dominance or leadership.
            </p>
        </div>
    );
};

// Ranked evidence-linked dimension bars with top-3 emphasis. Categories can
// overlap per evidence item — never a 100% composition, so no pie/donut.
const DimensionBars = ({ items, denominator, testId }) => {
    const rows = (items || []).filter((x) => (x.evidenceCount || 0) > 0);
    if (!rows.length) return <div className="text-[10px] text-[hsl(var(--mute))] italic">No published mentions.</div>;
    const max = rows[0].evidenceCount || 1;
    return (
        <div className="space-y-1" data-testid={testId}>
            {rows.slice(0, 8).map((r, i) => (
                <div key={r.key} className="flex items-center gap-2">
                    <span className={`text-[10px] w-28 truncate flex-shrink-0 ${i < 3 ? "font-semibold" : "text-[hsl(var(--ink-soft))]"}`} title={r.key}>{r.key}</span>
                    <div className="flex-1 h-2 rounded-sm bg-[hsl(var(--surface-sunken))] overflow-hidden">
                        <div className="h-full rounded-sm bg-[hsl(var(--accent))]" style={{ width: `${Math.max(3, (r.evidenceCount / max) * 100)}%`, opacity: i < 3 ? 0.85 : 0.35 }} />
                    </div>
                    <span className="num text-[9px] w-16 text-right text-[hsl(var(--mute))] flex-shrink-0">
                        {r.evidenceCount}{denominator ? ` · ${Math.round((r.evidenceCount / denominator) * 100)}%` : ""}
                    </span>
                </div>
            ))}
        </div>
    );
};

export default function CompanyDetail() {
    const { corgId } = useParams();
    const { status, data, error } = useWp12(`organizations/${encodeURIComponent(corgId)}`);
    const [showAllCo, setShowAllCo] = useState(false);

    if (status === "loading") return <div className="p-8 text-sm text-[hsl(var(--mute))]" data-testid="wp13-company-loading">Loading…</div>;
    if (status === "error" || !data?.organization) {
        return (
            <div className="p-8 space-y-2" data-testid="wp13-company-notfound">
                <p className="text-sm">Company not found in the active publication package{error ? ` (${error})` : ""}.</p>
                <Link to="/market-v2/organizations" className="text-xs text-[hsl(var(--accent-ink))] hover:underline">← Back to Companies</Link>
            </div>
        );
    }

    const org = data.organization;
    const stats = org.stats || {};
    const oa = org.observedAttributes || {};
    const footprint = data.patternFootprint || [];
    const zonesObserved = new Set(footprint.map((f) => f.marketZoneKey).filter(Boolean)).size;
    const evidenceDen = stats.evidenceCountLifetime || null;

    return (
        <div className="max-w-5xl space-y-7" data-testid="wp13-company-detail">
            {/* ── Company identity + descriptive facts ─────────────────────────── */}
            <div>
                <Link to="/market-v2/organizations" className="text-[10px] text-[hsl(var(--mute))] hover:text-[hsl(var(--accent-ink))] hover:underline">← Companies</Link>
                <div className="flex items-baseline gap-2.5 flex-wrap mt-1">
                    <h1 className="text-xl font-semibold" data-testid="wp13-company-name">{org.displayName}</h1>
                </div>
                <div className="card-flat p-4 mt-3 flex items-start gap-x-8 gap-y-3 flex-wrap" data-testid="wp13-company-stats">
                    <Stat label="Evidence mentions" value={stats.evidenceCountLifetime} testId="wp13-stat-evidence" />
                    <Stat label="Distinct sources" value={stats.sourceCountLifetime} testId="wp13-stat-sources" />
                    <Stat label="Weeks observed" value={stats.weeksObserved} testId="wp13-stat-weeks" />
                    <Stat label="First seen" value={stats.firstSeenWeek} testId="wp13-stat-first" />
                    <Stat label="Last seen" value={stats.lastSeenWeek} testId="wp13-stat-last" />
                    <Stat label="Associated patterns" value={stats.linkedPatternCountLifetime} testId="wp13-stat-patterns" />
                    <Stat label="Market zones observed" value={zonesObserved} testId="wp13-stat-zones" />
                </div>
            </div>

            {/* ── Activity over time ───────────────────────────────────────────── */}
            <Section title="Observed activity over time" testId="wp13-company-activity-section"
                     tip="Weekly published evidence activity for this company — observation coverage over time, rendered exactly as published. It is not a strength, momentum or importance measure."
                     tipId="wp13-tip-company-activity">
                <ActivityTimeline timeline={org.weeklyTimeline} stats={stats} />
            </Section>

            {/* ── Observed market footprint: Zone → Use Case → Pattern ─────────── */}
            <Section title="Observed market footprint" testId="wp13-company-footprint"
                     tip="Distribution of this company's pattern-linked observations across Market Zones, Use Cases and Patterns. One published evidence item may support multiple Patterns, so linked observations can exceed the number of unique Evidence records. A high share only means more of this company's pattern-linked observations sit in that area — never market share or strength."
                     tipId="wp13-tip-footprint">
                <MarketFootprint footprint={footprint} />
            </Section>

            {/* ── Evidence-linked dimensions ────────────────────────────────────── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                <div className="card-flat p-4" data-testid="wp13-company-geography">
                    <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] mb-2 inline-flex items-center gap-1.5">
                        Evidence-linked geography
                        <InfoTip label="Evidence-linked geography" testId="wp13-tip-company-geo">
                            Locations attached to the published evidence in which this company is mentioned. This is not company domicile, headquarters, legal presence or regional market strength.
                        </InfoTip>
                    </div>
                    <DimensionBars items={oa.geographies} denominator={evidenceDen} testId="wp13-company-geo-bars" />
                </div>
                <div className="card-flat p-4" data-testid="wp13-company-eventmix">
                    <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] mb-2 inline-flex items-center gap-1.5">
                        Event type mix
                        <InfoTip label="Event type mix" testId="wp13-tip-company-events">
                            Published event classification of the evidence mentioning this company. Each evidence item carries one event type, so the segments form a whole.
                        </InfoTip>
                    </div>
                    <CompositionBar segments={(oa.eventTypes || []).slice(0, 8).map((e) => ({ label: e.key, count: e.evidenceCount }))} testId="wp13-company-event-composition" />
                </div>
                <div className="card-flat p-4" data-testid="wp13-company-chains">
                    <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] mb-2 inline-flex items-center gap-1.5">
                        Evidence-linked blockchains
                        <InfoTip label="Evidence-linked blockchains" testId="wp13-tip-company-chains">
                            Blockchains referenced in the evidence mentioning this company. One evidence item can reference several blockchains, so counts overlap and percentages do not sum to 100%. This does not imply the company owns or operates these networks.
                        </InfoTip>
                    </div>
                    <DimensionBars items={oa.blockchains} denominator={evidenceDen} testId="wp13-company-chain-bars" />
                    <p className="text-[8.5px] text-[hsl(var(--mute))] mt-2">% of this company's evidence mentioning the blockchain — categories can overlap.</p>
                </div>
                <div className="card-flat p-4" data-testid="wp13-company-assets">
                    <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] mb-2 inline-flex items-center gap-1.5">
                        Evidence-linked asset classes
                        <InfoTip label="Evidence-linked asset classes" testId="wp13-tip-company-assets">
                            Asset classes referenced in the evidence mentioning this company. One evidence item can reference several asset classes, so counts overlap and percentages do not sum to 100%.
                        </InfoTip>
                    </div>
                    <DimensionBars items={oa.assetClasses} denominator={evidenceDen} testId="wp13-company-asset-bars" />
                    <p className="text-[8.5px] text-[hsl(var(--mute))] mt-2">% of this company's evidence mentioning the asset class — categories can overlap.</p>
                </div>
            </div>

            {/* ── Co-mentions ──────────────────────────────────────────────────── */}
            {(data.coMentions || []).length > 0 && (
                <Section title="Frequently co-mentioned companies" testId="wp13-company-comentions"
                         tip="Companies appearing in the same published evidence context; this does not imply a formal relationship, partnership or affiliation."
                         tipId="wp13-tip-comentions">
                    <div className="card-flat p-4 space-y-1">
                        {(showAllCo ? data.coMentions : data.coMentions.slice(0, 6)).map((c) => (
                            <div key={c.canonicalOrganizationId} className="flex items-center gap-2">
                                <Link to={`/market-v2/organizations/${encodeURIComponent(c.canonicalOrganizationId)}`}
                                      className="text-[10.5px] w-44 truncate flex-shrink-0 hover:text-[hsl(var(--accent-ink))] hover:underline"
                                      data-testid={`wp13-comention-${c.canonicalOrganizationId}`}>
                                    {c.displayName}
                                </Link>
                                <div className="flex-1 h-2 rounded-sm bg-[hsl(var(--surface-sunken))] overflow-hidden">
                                    <div className="h-full rounded-sm bg-[hsl(var(--accent))]/50"
                                         style={{ width: `${Math.max(3, (c.evidenceCount / (data.coMentions[0]?.evidenceCount || 1)) * 100)}%` }} />
                                </div>
                                <span className="num text-[9px] w-6 text-right text-[hsl(var(--mute))]">{c.evidenceCount}</span>
                            </div>
                        ))}
                        {data.coMentions.length > 6 && (
                            <button type="button" onClick={() => setShowAllCo((v) => !v)} data-testid="wp13-comentions-toggle"
                                    className="text-[10px] text-[hsl(var(--accent-ink))] hover:underline mt-1">
                                {showAllCo ? "Show top 6" : `View all ${data.coMentions.length}`}
                            </button>
                        )}
                    </div>
                </Section>
            )}

            {/* ── Recent evidence ──────────────────────────────────────────────── */}
            <Section title="Recent evidence" testId="wp13-company-evidence"
                     tip="Latest published evidence mentioning this company, rendered from the authoritative evidence records. Evidence volume reflects observation coverage."
                     tipId="wp13-tip-company-evidence">
                <div className="text-[10px] text-[hsl(var(--mute))]">{data.evidenceTotal ?? 0} published evidence records mention this company.</div>
                <div className="space-y-2" data-testid="wp13-company-evidence-list">
                    {(data.recentEvidence || []).map((e) => (
                        <div key={e.evidenceId} className="card-flat p-3.5">
                            <div className="flex items-baseline justify-between gap-3">
                                <a href={e.sourceUrl} target="_blank" rel="noreferrer"
                                   className="text-[11.5px] font-medium leading-snug hover:text-[hsl(var(--accent-ink))] hover:underline min-w-0"
                                   data-testid={`wp13-company-evidence-${e.evidenceId}`}>
                                    {e.displayTitle || e.sourceDomain}
                                </a>
                                <span className="num text-[9px] text-[hsl(var(--mute))] flex-shrink-0">{e.evidenceDate}</span>
                            </div>
                            <div className="text-[9px] text-[hsl(var(--mute))] mt-0.5">
                                {e.sourceDomain}{e.newsEventType ? ` · ${e.newsEventType}` : ""}{e.primaryUseCaseLabel ? ` · ${e.primaryUseCaseLabel}` : ""}
                            </div>
                            {e.summary && <p className="text-[10.5px] text-[hsl(var(--ink-soft))] leading-snug mt-1 line-clamp-2">{e.summary}</p>}
                        </div>
                    ))}
                </div>
            </Section>
        </div>
    );
}
